// AdminManagement.jsx
import React, { useMemo } from "react";
import { Card, Input, Dropdown, Menu } from "antd";
import { CloseOutlined, UserAddOutlined } from "@ant-design/icons";
import ChantPopConfirm from "../../../CommonComponents/ChantTooltip/ChantPopConfirm";
import ChantButton from "../../../CommonComponents/ChantButton/ChantButton";
import { ChantInput, ChantSelect } from "../../../CommonComponents";

const AdminManagement = ({
  // Props for new admins section
  admins = [],
  isParent = false,
  groupType = "",
  onAdminChange,
  onAddAdmin,
  onRemoveAdmin,
  onAdminTypeChange,

  // Props for existing admins (edit mode)
  editMode = false,
  registeredAdmins = [],
  unregisteredAdmins = [],
  onSendInvite,
  onRemoveUnregisteredAdmin,
  onMenuClick,
}) => {
  const menu = ({ id, name, userRole }) => (
    <Menu onClick={({ key }) => onMenuClick(key, id, name)}>
      {isParent && (
        <Menu.Item key="parentAdmin" disabled={userRole === "parentAdmin"}>
          Parent Admin
        </Menu.Item>
      )}
      <Menu.Item key="admin" disabled={userRole === "admin"}>
        Group Admin
      </Menu.Item>
      <Menu.Item key="member">Member</Menu.Item>
    </Menu>
  );

  const adminTypes = useMemo(() => {
    let types =
      groupType.toLowerCase() === "club"
        ? ["Parent Admin", "Admin"]
        : ["Admin"];
    return types.map((type) => ({ value: type, label: type }));
  }, [groupType]);

  return (
    <Card
      title="Manage Admins"
      style={{
        marginTop: "24px",
        marginBottom: "24px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      }}
    >
      {/* Add New Admins Section */}
      <div style={{ marginBottom: "32px" }}>
        <h5 style={{ marginBottom: "16px", color: "#595959" }}>
          Add New Admins
        </h5>
        {admins.map((admin, index) => (
          <Card
            title={`Admin ${index + 1}`}
            extra={<CloseOutlined onClick={() => onRemoveAdmin(index)} />}
            key={index}
            style={{
              marginBottom: "16px",
              backgroundColor: "#fafafa",
              border: "1px solid #d9d9d9",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: "16px",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <ChantInput
                label={`Name`}
                onChange={(e) => onAdminChange("name", e.target.value, index)}
                value={admin.name}
                required
                placeholder="Enter name"
              />

              <ChantInput
                label={`Email`}
                onChange={(e) => onAdminChange("email", e.target.value, index)}
                value={admin.email}
                required
                placeholder="Enter email"
              />

              <ChantSelect
                label="Role"
                onChange={(e) => onAdminTypeChange(e, index)}
                value={admin.adminType}
                required
                placeholder="--Select Type--"
                options={adminTypes}
              />

              {index > 0 && (
                <div
                  onClick={() => onRemoveAdmin(index)}
                  style={{
                    cursor: "pointer",
                    padding: "8px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <CloseOutlined
                    style={{
                      fontSize: "20px",
                      color: "#ff4d4f",
                    }}
                  />
                </div>
              )}
            </div>
          </Card>
        ))}

        <ChantButton
          type="primary"
          icon={<UserAddOutlined />}
          onClick={onAddAdmin}
        >
          Add Another Admin
        </ChantButton>
      </div>

      {/* Existing Admins Section (Edit Mode Only) */}
      {editMode && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "24px",
            paddingTop: "24px",
            borderTop: "1px solid #f0f0f0",
          }}
        >
          {/* Unregistered Admins */}
          <div>
            <h5 style={{ marginBottom: "16px", color: "#595959" }}>
              Pending Invitations
            </h5>
            {unregisteredAdmins.length === 0 ? (
              <div
                style={{
                  padding: "24px",
                  textAlign: "center",
                  backgroundColor: "#fafafa",
                  borderRadius: "4px",
                  color: "#8c8c8c",
                }}
              >
                No pending invitations
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                {unregisteredAdmins.map((admin, index) => (
                  <Card
                    key={admin.id || index}
                    size="small"
                    style={{ backgroundColor: "#fff9e6" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <Input disabled value={admin.id} style={{ flex: 1 }} />
                      <ChantPopConfirm
                        title="Send invitation email?"
                        onConfirm={() => {
                          if (!admin.emailInviteSent) {
                            onSendInvite(admin.id, index, {
                              name: admin.name,
                              type: admin.userRole || "admin",
                            });
                          }
                        }}
                        okText="Yes"
                        cancelText="No"
                      >
                        <ChantButton
                          size="small"
                          type={admin.emailInviteSent ? "default" : "primary"}
                          disabled={admin.emailInviteSent}
                        >
                          {admin.emailInviteSent
                            ? "Invite Sent"
                            : "Send Invite"}
                        </ChantButton>
                      </ChantPopConfirm>
                      <ChantPopConfirm
                        title="Remove this admin?"
                        onConfirm={() => onRemoveUnregisteredAdmin(admin.id)}
                        okText="Yes"
                        cancelText="No"
                      >
                        <ChantButton size="small" danger>
                          Remove
                        </ChantButton>
                      </ChantPopConfirm>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Registered Admins */}
          <div>
            <h5 style={{ marginBottom: "16px", color: "#595959" }}>
              Active Admins
            </h5>
            {registeredAdmins.length === 0 ? (
              <div
                style={{
                  padding: "24px",
                  textAlign: "center",
                  backgroundColor: "#fafafa",
                  borderRadius: "4px",
                  color: "#8c8c8c",
                }}
              >
                No active admins found
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                  gap: "12px",
                }}
              >
                {registeredAdmins.map((admin) => (
                  <Card
                    key={admin.id || admin.name}
                    size="small"
                    style={{ backgroundColor: "#f0f5ff" }}
                  >
                    <div style={{ marginBottom: "8px" }}>
                      <Input
                        disabled
                        value={admin.name}
                        style={{
                          fontWeight: "500",
                          backgroundColor: "white",
                        }}
                      />
                    </div>
                    <Dropdown overlay={menu(admin)} trigger={["click"]}>
                      <ChantButton size="small" block>
                        Manage Role
                      </ChantButton>
                    </Dropdown>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
};

export default AdminManagement;
